
import { TFuncaoFinanceiro } from '../../../../../../types/api/financeiro/funcoes-financeiro'
import { TOpenModalFuncoesFinanceiras } from '..';
import { useMutation, useQueryClient } from 'react-query';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useMemo } from 'react';
import axiosClient from '../../../../../../lib/axios';
import { enqueueSnackbar } from 'notistack';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Grid, TextField } from '@mui/material';
import RadioSimplesSimOuNao from '../../../../../../components/RadioSimplesSimOuNao';

export default function DialogCriacaoEditFuncoes({
    defaultValue,
    open,
    handleClose
}: DialogCriacaoEditFuncoesProps) {

    const queryClient = useQueryClient();

    //Valores iniciais
    const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue])
    const valoresIniciais = useMemo(() => defaultValue?.defaultValue ? defaultValue?.defaultValue : null, [defaultValue])
    //

    const methods = useForm<TFuncaoFinanceiro>({
        defaultValues: {
            id: valoresIniciais?.id,
            nome: valoresIniciais?.nome,
            considera_fechamento_cc: valoresIniciais?.considera_fechamento_cc || 'N',
            considera_fluxo_caixa: valoresIniciais?.considera_fluxo_caixa || 'N',
            flg_troca_funcao: valoresIniciais?.flg_troca_funcao || 'N',
        }
    })
    const { control, handleSubmit, getFieldState, formState: { errors } } = methods;

    //Querys e mutations
    const { mutate: criarFuncao, isLoading: isLoadingCriarFuncoes } = useMutation({
        mutationKey: 'cadastrar-funcao',
        mutationFn: (inputs: any) => axiosClient.post("financeiro/configuracoes/cadastrar-funcao", inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao cadastrar função!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["pesquisar-funcoes"])
            handleClose()
            enqueueSnackbar('Cadastro de função realizado com sucesso!', { variant: 'success' });
        }
    })

    const { mutate: atualizarFuncao, isLoading: isLoadingAttFuncao } = useMutation({
        mutationKey: 'atualizar-funcao',
        mutationFn: (inputs: any) => axiosClient.put(`financeiro/configuracoes/atualizar-funcao/${valoresIniciais?.id}`, inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao atualizar função!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["pesquisar-funcoes"])
            handleClose()
            enqueueSnackbar('Atualização de função com sucesso!', { variant: 'success' });
        }
    })
    //

    //Funções manipuladoras
    const onSubmit = (data: TFuncaoFinanceiro) => {
        const params = {
            nome: data.nome,
            id: data.id,
            considera_fechamento_cc: data.considera_fechamento_cc,
            considera_fluxo_caixa: data.considera_fluxo_caixa,
            flg_troca_funcao: data.flg_troca_funcao,
        }
        if (isEdit) {
            atualizarFuncao(params)
        }
        if (!isEdit) {
            criarFuncao(params)
        }
    }
    //

    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {`${!isEdit ? "Adicionar" : "Editar"} função financeira`}
                </DialogTitle>

                {
                    (isLoadingAttFuncao || isLoadingCriarFuncoes) &&
                    <Box display="flex" justifyContent="center" mt={4} mb={8}>
                        <CircularProgress />
                    </Box>
                }

                {
                    !isLoadingAttFuncao && !isLoadingCriarFuncoes &&
                    <>
                        <Box
                            component={'form'}
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <DialogContent>
                                <Grid container spacing={2}>
                                    <Grid size={{xs: 6}}>
                                        <Controller
                                            name="id"
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    defaultValue={valoresIniciais?.id}
                                                    label="Código*"
                                                    variant="outlined"
                                                    type='number'
                                                    fullWidth
                                                    error={getFieldState('id').invalid}
                                                    helperText={getFieldState('id').error?.message || ""}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid size={{xs: 6}}>
                                        <Controller
                                            name="nome"
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    defaultValue={valoresIniciais?.nome}
                                                    label="Nome*"
                                                    variant="outlined"
                                                    fullWidth
                                                    error={getFieldState('nome').invalid}
                                                    helperText={getFieldState('nome').error?.message || ""}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid size={{xs: 7}}>
                                        <RadioSimplesSimOuNao
                                            {...{ valoresIniciais }}
                                            nomeForm='considera_fechamento_cc'
                                            nomeCampo='Considera fechamento C/C Consumo?'
                                        />
                                    </Grid>
                                    <Grid size={{xs: 5}}>
                                        <RadioSimplesSimOuNao
                                            {...{ valoresIniciais }}
                                            nomeForm='flg_troca_funcao'
                                            nomeCampo='Permite troca?'
                                        />
                                    </Grid>
                                    <Grid size={{xs: 6}}>
                                        <RadioSimplesSimOuNao
                                            {...{ valoresIniciais }}
                                            nomeForm='considera_fluxo_caixa'
                                            nomeCampo='Considera fluxo caixa?'
                                        />
                                    </Grid>
                                </Grid>
                                <Box mt={2} width={'100%'} display={'flex'} justifyContent={'flex-end'} alignItems={'center'} gap={2}>
                                    <Button
                                        onClick={handleClose}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        autoFocus
                                        disabled={isLoadingCriarFuncoes || isLoadingAttFuncao}

                                    >
                                        {
                                            (isLoadingCriarFuncoes || isLoadingAttFuncao) &&
                                            <CircularProgress size={20} sx={{ marginRight: 1 }} />
                                        }
                                        {!isEdit ? "Adicionar" : "Atualizar"}
                                    </Button>
                                </Box>
                            </DialogContent>
                        </Box>
                    </>
                }
            </Dialog>
        </FormProvider>
    )
}

interface DialogCriacaoEditFuncoesProps {
    defaultValue: TOpenModalFuncoesFinanceiras;
    open: boolean;
    handleClose: () => void;

}
